import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronsUpDown, FolderKanban, Plus, Star } from 'lucide-react';
import { apiFetch, v1Path } from '../../lib/api';
import { useSession } from '../session-context';
import StarProjectButton from './StarProjectButton';
import {
  clearLastProjectId,
  getLastProjectId,
  isProjectId,
  projectSwitchPath,
  setLastProjectId,
  useProjectIdFromPath,
} from '../../lib/project-path';
import { isWorkspaceProject } from '../../lib/workspace-projects';
import type { ProjectDto } from '../../lib/work-types';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useOverlays } from './overlay-context';

function isProjectDto(value: unknown): value is ProjectDto {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return (
    isProjectId(row.id) &&
    typeof row.name === 'string' &&
    row.name.trim().length > 0 &&
    typeof row.identifier === 'string' &&
    row.identifier.trim().length > 0
  );
}

export default function ProjectSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathProjectId = useProjectIdFromPath();
  const { starredProjectId } = useSession();
  const { pushBlock, popBlock, openCreateProject } = useOverlays();
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [shown, setShown] = useState<ProjectDto | null>(null);

  useEffect(() => {
    if (pathProjectId) setLastProjectId(pathProjectId);
  }, [pathProjectId]);

  useEffect(() => {
    const id = pathProjectId ?? getLastProjectId();
    if (!id) {
      setShown(null);
      return;
    }
    let cancelled = false;
    void apiFetch<unknown>(v1Path(`/projects/${id}`))
      .then((dto) => {
        if (cancelled) return;
        if (!isProjectDto(dto)) {
          throw new Error('Invalid project payload.');
        }
        if (isWorkspaceProject(dto)) {
          clearLastProjectId();
          setShown(null);
          return;
        }
        setShown(dto);
      })
      .catch(() => {
        if (cancelled) return;
        if (!pathProjectId) {
          clearLastProjectId();
          setShown(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [pathProjectId]);

  function loadList() {
    void apiFetch<unknown>(v1Path('/projects?limit=100&offset=0'))
      .then((res) => {
        if (!res || typeof res !== 'object' || !('items' in res) || !Array.isArray(res.items)) {
          throw new Error('Invalid projects list.');
        }
        setProjects(res.items.filter(isProjectDto).filter((project) => !isWorkspaceProject(project)));
      })
      .catch(() => {
        setProjects([]);
      });
  }

  function select(project: ProjectDto) {
    setOpen(false);
    setLastProjectId(project.id);
    setShown(project);
    navigate(projectSwitchPath(location.pathname, project.id));
  }

  const inProject = Boolean(pathProjectId);
  // Outside a stream the last-visited stream would be misleading, so the switcher just offers a jump.
  const current = inProject ? shown : null;
  const label = current
    ? `${current.identifier} ${current.name}`
    : 'Jump to a stream';
  const ordered = [...projects].sort((a, b) => {
    if (a.id === starredProjectId) return -1;
    if (b.id === starredProjectId) return 1;
    return 0;
  });

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          pushBlock();
          loadList();
        } else {
          popBlock();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="min-w-0 max-w-[16rem] gap-1.5 font-normal"
          aria-label="Switch stream"
        >
          <FolderKanban className="h-4 w-4 shrink-0" aria-hidden />
          <span
            className={
              inProject
                ? 'truncate'
                : 'truncate text-muted-foreground'
            }
          >
            {current ? (
              <>
                {starredProjectId === current.id ? (
                  <Star className="mr-1 inline h-3 w-3 fill-current" aria-hidden />
                ) : null}
                <span>{current.name}</span>
              </>
            ) : (
              label
            )}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <Command>
          <CommandInput placeholder="Search streams" />
          <CommandList>
            <CommandEmpty>No streams found.</CommandEmpty>
            <CommandGroup heading="Streams">
              {ordered.map((project) => (
                <CommandItem
                  key={project.id}
                  value={`${project.identifier} ${project.name}`}
                  onSelect={() => select(project)}
                >
                  <Check
                    className={
                      project.id === pathProjectId ? 'opacity-100' : 'opacity-0'
                    }
                  />
                  <span className="font-mono text-xs text-muted-foreground">
                    {project.identifier}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{project.name}</span>
                  <StarProjectButton projectId={project.id} className="ml-auto" />
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  setOpen(false);
                  navigate('/projects');
                }}
              >
                <FolderKanban />
                All streams
              </CommandItem>
              <CommandItem
                onSelect={() => {
                  setOpen(false);
                  openCreateProject();
                }}
              >
                <Plus />
                New stream
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
